import sendQuery from './send-query';

// Parent data shared by every tile of a combined listing family
const cache = new Map();
// Parents requested during the current tick, loaded together in one request
let pending = new Map();

const flush = async () => {
  const batch = pending;
  pending = new Map();

  // Each color of a combined listing is its own product and can have its own price, promo tags and flags, which Nosto
  // only gives us for the tile's own color. The parent exposes every child through its Color option:
  // optionValues.firstSelectableVariant is a variant of that child. Prices are returned in the current market's
  // currency. They are only requested when the other colors' swatches are shown.
  const include_colors = window.theme.productTiles?.combined_listings_show_swatches !== false;
  const ids = Array.from(batch.keys()).map((id) => `"gid://shopify/Product/${id}"`);

  try {
    const query = `
      query getCombinedListingParents @inContext(country: ${window.theme.country_code}, language: ${window.theme.language_code}) {
        nodes(ids: [${ids.join(',')}]) {
          ... on Product {
            id
            handle
            ${include_colors ? `options(first: 10) {
              name
              optionValues {
                name
                firstSelectableVariant {
                  product {
                    tags
                    promoTags: metafield(namespace: "astr", key: "promo_tags") {
                      value
                    }
                    bestseller30: metafield(namespace: "astr", key: "bestseller_product_30") {
                      value
                    }
                    bestseller14: metafield(namespace: "astr", key: "bestseller_product_14") {
                      value
                    }
                    variants(first: 100) {
                      nodes {
                        styleFlag: metafield(namespace: "astr", key: "style_flag") {
                          value
                        }
                      }
                    }
                    priceRange {
                      minVariantPrice { amount }
                      maxVariantPrice { amount }
                    }
                    compareAtPriceRange {
                      maxVariantPrice { amount }
                    }
                  }
                }
              }
            }` : ''}
          }
        }
      }
    `;

    const response = await sendQuery(query);
    const json = await response.json();
    const parents = new Map();

    (json?.data?.nodes || []).forEach((node) => {
      if (!node?.id) {
        return;
      }

      const colors = new Map();
      // The parent also lists the Size values, which all point to the first child
      (node.options || []).filter((option) => option.name.toLowerCase() === 'color').forEach((option) => {
        option.optionValues.forEach((option_value) => {
          const variant = option_value.firstSelectableVariant;
          if (!variant?.product) {
            return;
          }

          colors.set(option_value.name.trim().toLowerCase(), {
            min_price: Number(variant.product.priceRange.minVariantPrice.amount),
            max_price: Number(variant.product.priceRange.maxVariantPrice.amount),
            compare_at_price: Number(variant.product.compareAtPriceRange.maxVariantPrice.amount),
            // Same format as Nosto's astr-promo_tags custom field
            promo_tags: variant.product.promoTags?.value || '',
            tags: variant.product.tags || [],
            bestseller_30: variant.product.bestseller30?.value,
            bestseller_14: variant.product.bestseller14?.value,
            // Same as updateTileFlag(): the 1st variant value if any
            style_flag: variant.product.variants.nodes.find((node) => node.styleFlag?.value)?.styleFlag.value || '',
          });
        });
      });

      parents.set(node.id.replace('gid://shopify/Product/', ''), { handle: node.handle, colors });
    });

    batch.forEach((deferred, id) => {
      if (parents.has(id)) {
        deferred.resolve(parents.get(id));
      } else {
        deferred.reject(new Error(`[ASTR] Combined listing parent ${id} not found`));
      }
    });
  } catch (error) {
    batch.forEach((deferred) => deferred.reject(error));
  }
};

/**
 * Tiles initialized together (a page of products, search results...) share a single request.
 * @param {String|Number} parent_id - Parent product id (astr-combined_product_parent_id)
 * @returns {Promise<{handle: String, colors: Map<String, {min_price: Number, max_price: Number, compare_at_price: Number, promo_tags: String, tags: Array, bestseller_30: String, bestseller_14: String, style_flag: String}>}>}
 * colors is keyed by lowercased color name, empty when the prices aren't requested
 */
export default (parent_id) => {
  const key = String(parent_id);

  if (!cache.has(key)) {
    const request = new Promise((resolve, reject) => {
      if (pending.size === 0) {
        setTimeout(flush, 0);
      }
      pending.set(key, { resolve, reject });
    });

    // Allow a retry on the next swatch selection instead of caching the failure
    request.catch(() => cache.delete(key));
    cache.set(key, request);
  }

  return cache.get(key);
};
